import React, {ReactNode, useEffect} from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import {UiConfigurationApi} from '@deviceinsight/ng-ui-scale-lib/api';
import type {Asset} from '@deviceinsight/ng-ui-api-client';
import {GridColorPicker} from '@deviceinsight/ng-ui-components';
import {Button, Checkbox} from '@deviceinsight/ng-ui-basic-components';
import {getAssetDeviceType} from '../../ruleengine/ruleEditHelpers';
import {
	BLINKING_FREQUENCY,
	COLOR_PALETTE,
	DeviceType,
	flashingCheckBoxMessage
} from '../../../modules/ruleengine/config/consts';
import {setBlink} from '../../../api/actionsApi';
import {
	DATAPOINT_BUTTON1,
	DATAPOINT_BUTTON2,
	DATAPOINT_BUTTON_COLOR1,
	DATAPOINT_BUTTON_COLOR2,
	DATAPOINT_BUTTON_FREQUENCY1,
	DATAPOINT_BUTTON_FREQUENCY2,
	DATAPOINT_LED,
	DATAPOINT_LED_COLOR,
	DATAPOINT_LED_FREQUENCY
} from '../../../modules/common/consts';
import directMethodCall from './directMethodCall';
import {AssetLiveData} from '../common/useAssetSubscription';
type Props = {
	asset: Asset;
	assetLiveData: AssetLiveData;
	canSetColor: boolean;
};
type BlinkWithColorProps = {
	asset: Asset;
	dataPoint: string;
	children: ReactNode;
	currentColor: string | null | undefined;
	canSetColor: boolean;
	frequency: number | null | undefined;
};

const BlinkWithColorButton = ({
	asset,
	dataPoint,
	children,
	currentColor,
	canSetColor,
	frequency
}: BlinkWithColorProps) => {
	const [color, setColor] = React.useState<string | null | undefined>(currentColor);
	const [flashing, setFlashing] = React.useState<boolean>(false);
	useEffect(() => {
		setFlashing(frequency ? true : false);
	}, [frequency]);
	const isDirectMethodEnabled = UiConfigurationApi.isAppEnabled('directMethod');
	useEffect(() => {
		setColor(currentColor);
	}, [currentColor]);
	return (
		<div className="info-row">
			<label>{children}</label>
			{canSetColor ? (
				<div className="button-tools">
					{isDirectMethodEnabled && (
						<Button
							data-testid={`direct-method_${dataPoint}`}
							naked
							onClick={() => directMethodCall(asset, dataPoint, color || currentColor, flashing)}>
							<div className="blink-icon direct" />
						</Button>
					)}
					<Button
						data-testid={`set_${dataPoint}`}
						primary
						onClick={() =>
							setBlink(asset, dataPoint, color || currentColor, flashing ? BLINKING_FREQUENCY : 0)
						}>
						<Trans i18nKey="dashboard.widgets.digitaltwin.setcolor">Set</Trans>
					</Button>
					<GridColorPicker
						inline={false}
						colors={COLOR_PALETTE}
						value={color || currentColor || ''}
						onPick={(newColor) => setColor(newColor ? newColor.toUpperCase() : undefined)}
					/>
					<Checkbox
						value={flashing}
						title={flashingCheckBoxMessage}
						label={flashingCheckBoxMessage}
						onChange={() => setFlashing(!flashing)}
						data-testid={`flashing_${dataPoint}`}
					/>
				</div>
			) : (
				<div
					className="dead-colorpicker"
					style={{
						background: (color || currentColor) ?? undefined
					}}
					data-testid="colorpicker-dead"
				/>
			)}
		</div>
	);
};

const BlinkTrigger = ({asset, assetLiveData, canSetColor}: Props) => {
	const type = getAssetDeviceType(asset);

	switch (type) {
		case DeviceType.sLight:
			return (
				<BlinkWithColorButton
					canSetColor={canSetColor}
					asset={asset}
					dataPoint={DATAPOINT_LED}
					currentColor={assetLiveData[DATAPOINT_LED_COLOR]}
					frequency={assetLiveData[DATAPOINT_LED_FREQUENCY]}>
					<Trans i18nKey="dashboard.widgets.digitaltwin.activateblink.led">LED</Trans>
				</BlinkWithColorButton>
			);

		case DeviceType.sBox:
			return (
				<div className="trigger-blink-buttons">
					<BlinkWithColorButton
						canSetColor={canSetColor}
						asset={asset}
						dataPoint={DATAPOINT_BUTTON2}
						currentColor={assetLiveData[DATAPOINT_BUTTON_COLOR2]}
						frequency={assetLiveData[DATAPOINT_BUTTON_FREQUENCY2]}>
						<Trans i18nKey="dashboard.widgets.digitaltwin.activateblink.button2">Button 2</Trans>
					</BlinkWithColorButton>
					<BlinkWithColorButton
						canSetColor={canSetColor}
						asset={asset}
						dataPoint={DATAPOINT_BUTTON1}
						currentColor={assetLiveData[DATAPOINT_BUTTON_COLOR1]}
						frequency={assetLiveData[DATAPOINT_BUTTON_FREQUENCY1]}>
						<Trans i18nKey="dashboard.widgets.digitaltwin.activateblink.button1">Button 1</Trans>
					</BlinkWithColorButton>
				</div>
			);

		case DeviceType.kisIo:
			return <div />;

		// no blinking available
		default:
			return <div />;
	}
};

export default BlinkTrigger;
